import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Sparkle, ArrowRight, ArrowLeft, Lock, EnvelopeSimple, ShieldCheck, Eye, EyeSlash } from "@phosphor-icons/react";

const REMEMBER_EMAIL_KEY = "tf_remember_email";

export default function Login() {
  const { user, login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_EMAIL_KEY) || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(REMEMBER_EMAIL_KEY));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [pendingState, setPendingState] = useState(null); // {code, message}

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/client"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setPendingState(null);
    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const u = await login(cleanEmail, password);
      if (rememberMe) localStorage.setItem(REMEMBER_EMAIL_KEY, cleanEmail);
      else localStorage.removeItem(REMEMBER_EMAIL_KEY);
      navigate(u.role === "admin" ? "/admin" : "/client");
    } catch (e) {
      const d = e?.response?.data?.detail;
      // Backend returns dict {code, message} for pending/rejected status
      if (d && typeof d === "object" && d.code) {
        setPendingState(d);
      } else {
        setErr(typeof d === "string" ? d : t("invalid_credentials"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (pendingState) {
    return <PendingActivationScreen state={pendingState} onBack={() => setPendingState(null)} />;
  }

  return (
    <div className="min-h-screen bg-[var(--tf-bg)] flex flex-col">
      <div className="tf-cmyk-strip" />

      {/* Minimal top bar */}
      <header className="px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="font-display font-black text-lg tracking-tight text-[var(--tf-ink)]" data-testid="header-brand">
          Teefarm Connect
        </Link>
        <div className="text-xs text-[var(--tf-muted)] hidden md:block">
          {t("login_help_needed")} <a href="mailto:info@tee.farm" className="underline text-[var(--tf-ink)]">info@tee.farm</a>
        </div>
      </header>

      {/* Main gateway */}
      <main className="flex-1 flex items-center justify-center px-4 md:px-8 py-8">
        <div className="w-full max-w-5xl grid md:grid-cols-2 gap-6 md:items-stretch">
          {/* LEFT: Nuovo cliente */}
          <section className="tf-card p-8 md:p-12 relative bg-gradient-to-br from-[var(--tf-primary)]/25 to-[var(--tf-primary)]/5 flex flex-col overflow-hidden" data-testid="signup-panel">
            <div className="tf-label flex items-center gap-2">
              <Sparkle size={12} weight="fill" /> {t("login_new_client")}
            </div>
            <h1 className="font-display font-black text-3xl md:text-4xl mt-2 tracking-tight">{t("login_hero_title")}</h1>
            <p className="text-sm text-[var(--tf-muted)] mt-3 leading-relaxed max-w-sm">
              {t("login_hero_desc")}
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              {[t("login_feat_1"), t("login_feat_2"), t("login_feat_3")].map((f, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--tf-ink)]" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-auto pt-8">
              <Link
                to="/signup"
                className="tf-pill-primary w-full"
                data-testid="signup-cta"
              >
                {t("login_create_account")} <ArrowRight size={16} weight="bold" />
              </Link>
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-[var(--tf-muted)]">
              <Sparkle size={14} weight="fill" className="text-[var(--tf-ink)]" />
              {t("login_hero_footer")}
            </div>
          </section>

          {/* RIGHT: Cliente esistente */}
          <section
            className="tf-card p-8 md:p-12 bg-white flex flex-col overflow-hidden"
            data-testid="login-panel"
          >
            <div className="flex items-center justify-between">
              <div className="tf-label flex items-center gap-2">
                <Lock size={12} weight="bold" /> {t("login_existing_client")}
              </div>
              <LanguageSwitcher />
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-2 tracking-tight">{t("login_welcome_back_title")}</h2>
            <p className="text-sm text-[var(--tf-muted)] mt-3">
              {t("login_subtitle2")}
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4 flex-1 flex flex-col">
              <div>
                <label className="tf-label block mb-1.5">{t("email")}</label>
                <div className="relative">
                  <EnvelopeSimple size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tf-muted)] pointer-events-none" />
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="tf-input !pl-11 !py-3.5"
                    placeholder="tu@azienda.com"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label className="tf-label block mb-1.5">{t("password")}</label>
                <div className="relative">
                  <Lock size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--tf-muted)] pointer-events-none" />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="tf-input !pl-11 !pr-11 !py-3.5"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center text-[var(--tf-muted)] hover:text-[var(--tf-ink)] hover:bg-[var(--tf-bg)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tf-primary)]"
                    data-testid="password-reveal-toggle"
                    aria-label={showPassword ? t("pw_hide_label") : t("pw_show_label")}
                  >
                    {showPassword ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                  </button>
                </div>
                {err && (
                  <div className="text-xs text-[var(--tf-error)] mt-1.5 flex items-center gap-1" data-testid="login-error">
                    <span className="w-1 h-1 rounded-full bg-[var(--tf-error)]" /> {err}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer select-none text-xs text-[var(--tf-muted)] hover:text-[var(--tf-ink)]" data-testid="remember-me-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 accent-[var(--tf-ink)]"
                    data-testid="remember-me-checkbox"
                  />
                  {t("login_remember_email")}
                </label>
                <Link to="/forgot-password" className="text-xs text-[var(--tf-ink)] hover:underline font-bold" data-testid="forgot-password-link">
                  {t("login_forgot_password")}
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="tf-pill-secondary w-full mt-auto"
                data-testid="login-submit"
              >
                {loading ? t("login_signing_in") : t("sign_in")}
              </button>
            </form>

            <div className="mt-4 flex items-center gap-2 text-xs text-[var(--tf-muted)]">
              <ShieldCheck size={14} weight="duotone" className="text-[var(--tf-ink)]" />
              {t("login_secure_session")}
            </div>
          </section>
        </div>
      </main>

      {/* Minimal footer */}
      <footer className="px-6 md:px-10 py-4 text-[11px] text-[var(--tf-muted)] flex flex-wrap items-center justify-between gap-2">
        <span>© Teefarm S.A.S. · P.IVA 05217980282</span>
        <div className="flex items-center gap-3">
          <Link to="/legal/terms" className="hover:text-[var(--tf-ink)]">{t("legal_terms_short")}</Link>
          <span className="opacity-30">·</span>
          <Link to="/legal/privacy" className="hover:text-[var(--tf-ink)]">{t("legal_privacy_short")}</Link>
          <span className="opacity-30">·</span>
          <Link to="/legal/cookies" className="hover:text-[var(--tf-ink)]">{t("legal_cookies_short")}</Link>
        </div>
      </footer>
    </div>
  );
}


function PendingActivationScreen({ state, onBack }) {
  const { t } = useI18n();
  const isRejected = state.code === "rejected";
  return (
    <div className="min-h-screen bg-[var(--tf-bg)] flex flex-col" data-testid="pending-activation-screen">
      <header className="px-6 md:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 font-display font-black text-lg tracking-tight">
          <Sparkle size={18} weight="fill" className="text-[var(--tf-ink)]" /> teefarm
        </Link>
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="w-full max-w-lg text-center">
          {isRejected ? (
            <div className="w-24 h-24 mx-auto rounded-full bg-[#FBDDE1] flex items-center justify-center">
              <Lock size={40} weight="fill" className="text-[#A11534]" />
            </div>
          ) : (
            <div className="relative w-24 h-24 mx-auto">
              <div className="absolute inset-0 rounded-full bg-[#CCF22C] animate-pulse-slow" />
              <div className="absolute inset-0 flex items-center justify-center animate-hourglass-rotate">
                <svg width="42" height="42" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[var(--tf-ink)]">
                  <path d="M6 2h12v3l-4.5 5.5v3L18 19v3H6v-3l4.5-5.5v-3L6 5V2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="currentColor" fillOpacity="0.15" />
                  <path d="M8 4h8v1c0 2-2 3.5-4 3.5S8 7 8 5V4z" fill="currentColor" />
                  <path d="M9 20h6c0-1.5-1.5-3-3-3s-3 1.5-3 3z" fill="currentColor" />
                </svg>
              </div>
            </div>
          )}
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--tf-muted)] mt-8 flex items-center justify-center gap-2">
            <Sparkle size={12} weight="fill" /> {isRejected ? t("login_rejected_title") : t("login_pending_title")}
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-4 tracking-tight text-[var(--tf-ink)]">
            {isRejected ? t("login_rejected_heading") : t("login_pending_heading")}
          </h1>
          <p className="text-base text-[var(--tf-muted)] mt-4 max-w-md mx-auto">
            {isRejected ? state.message : t("login_pending_body")}
          </p>
          <div className="bg-white border border-[var(--tf-border)] rounded-[24px] p-6 mt-8 text-left space-y-3" data-testid="pending-info-card">
            <div className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)]">{t("login_pending_whatnow")}</div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#CCF22C] text-[var(--tf-ink)] flex items-center justify-center font-black text-xs tf-numeric shrink-0">1</span>
              <div className="text-sm text-[var(--tf-ink)]"><b>{t("login_pending_step1_title")}</b> — {t("login_pending_step1_body")}</div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#CCF22C] text-[var(--tf-ink)] flex items-center justify-center font-black text-xs tf-numeric shrink-0">2</span>
              <div className="text-sm text-[var(--tf-ink)]"><b>{t("login_pending_step2_title")}</b> — {t("login_pending_step2_body")}</div>
            </div>
            <div className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-[#CCF22C] text-[var(--tf-ink)] flex items-center justify-center font-black text-xs tf-numeric shrink-0">3</span>
              <div className="text-sm text-[var(--tf-ink)]"><b>{t("login_pending_step3_title")}</b> — {t("login_pending_step3_body")}</div>
            </div>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button onClick={onBack} className="tf-pill-secondary" data-testid="pending-back-btn">
              <ArrowLeft size={14} weight="bold" /> {t("login_back_to_login")}
            </button>
            <a href="mailto:info@tee.farm" className="tf-pill-primary" data-testid="pending-contact-btn">
              {t("login_contact_support")}
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
